import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { connect } from 'react-redux';
import {
  FlexCol,
  FlexRow,
  ArticleList,
  SitemapList,
  Pagination,
} from '@bart.sk-eshop/eshop-defaults';
import MetaTags from '../../components/_helpers/MetaTags/MetaTags';
import {
  buildListingCanonicalUrl,
  buildMetaDescription,
  buildSectionMetaDescription,
  normalizeMetaText,
  NOINDEX_FOLLOW,
  shouldNoIndexByUrlParams,
} from '../../utilities/metatags';
import { loadUrlTranslations } from '../App/actions';
import { connectSsr } from '@bart.sk-eshop/ssr-service';
import { loadCMSCategoriesTree, loadCMSArticles } from './actions';
import {
  sitemapSelector,
  allArticlesSelector,
  articlesIsFetchingSelector,
  sitemapByIdSelector,
} from './selectors';
import API, { ThenArg } from '../../services/API';
import { withRouter, WithRouterProps } from 'react-router';
import { push } from 'react-router-redux';
import { prop } from '../../utilities';
import {
  setBreadCrumbPath,
  BreadCrumbType,
} from '../BreadCrumb/breadCrumbSlice';
import ToTopButton from '../../components/_helpers/Default/ToTopButton';

const ARTICLES_LIMIT = 6;

interface Props {
  dispatch: (action: any) => void;
  sitemap: ThenArg<typeof API.loadSitemapTree>['sitemap_tree'];
  allArticles: ThenArg<typeof API.loadArticles>;
  isFetchingArticles: boolean;
  sitemapsById: object;
  sitemapId: number;
  sectionName: string;
  sectionMeta: any;
  lang: string;
}

class CMSSectionArticles extends React.Component<Props & WithRouterProps> {
  public static async getInitialProps(parentProps) {
    const { dispatch, location, lang, sitemapId } = parentProps;
    const section = await API.loadSitemap(
      sitemapId.toString(),
      {},
      { xAcceptLanguage: lang },
    );
    const hubId = (
      prop(section, 'parent_id') ||
      prop(section, 'id') ||
      sitemapId
    ).toString();
    const offset = prop(location, 'query.offset', 0);

    await dispatch(loadCMSCategoriesTree(hubId));
    await dispatch(loadCMSArticles(hubId, sitemapId, ARTICLES_LIMIT, offset));
    await dispatch(
      loadUrlTranslations(location.pathname, location.pathname, 'sitemap'),
    );
    dispatch(setBreadCrumbPath(BreadCrumbType.CMS_ARTICLES));

    return {
      sitemapId,
      hubId,
      sectionName: prop(section, 'name', ''),
      sectionMeta: prop(section, 'content.json_content.meta', {}),
    };
  }

  public render() {
    const { lang, location, sectionName, sectionMeta } = this.props;

    return (
      <>
        <Wrapper className="container container--wide">
          <MetaTags
            tags={{
              title:
                normalizeMetaText(prop(sectionMeta, 'title')) || sectionName,
              description:
                buildMetaDescription([prop(sectionMeta, 'description')]) ||
                buildSectionMetaDescription(sectionName, lang),
            }}
            canonicalUrl={buildListingCanonicalUrl(
              prop(location, 'pathname'),
              prop(location, 'query', {}),
              lang,
            )}
            robots={
              shouldNoIndexByUrlParams(prop(location, 'query', {}))
                ? NOINDEX_FOLLOW
                : undefined
            }
          />
          {this.renderCategories()}
          {this.renderArticles()}
        </Wrapper>
        <ToTopButton />
      </>
    );
  }

  public renderCategories = () => {
    const { sitemap, sitemapId, sectionName } = this.props;
    const remappedSitemap = (Array.isArray(sitemap) ? sitemap : []).map(s => ({
      name: s.name || '',
      id: s.id || '',
    }));

    return (
      <SitemapList
        currentId={sitemapId ? sitemapId.toString() : -1}
        handleCategoryChange={this.handleCategoryChange}
        sitemaps={remappedSitemap}
        isBlog={true}
        title={sectionName}
      />
    );
  };

  public renderArticles = () => {
    const {
      allArticles,
      isFetchingArticles,
      location,
      sitemapsById,
    } = this.props;

    return (
      <ContentWrapper>
        <ArticlesWrapper>
          <ArticleList
            sitemapsById={sitemapsById}
            articles={prop(allArticles, 'articles')}
            isFetching={isFetchingArticles}
          />
          <Pagination
            query={`${prop(location, 'pathname')}?`}
            totalItems={prop(allArticles, 'total', 0)}
            limit={ARTICLES_LIMIT}
            offset={prop(location, 'query.offset', 0)}
            onOffsetChange={this.handleOffsetChange}
          />
        </ArticlesWrapper>
      </ContentWrapper>
    );
  };

  public handleOffsetChange = (newOffset: number) => {
    const { dispatch, location } = this.props;
    dispatch(
      push({
        pathname: prop(location, 'pathname'),
        search: newOffset ? `?offset=${newOffset}` : '',
      }),
    );
  };

  public handleCategoryChange = async (newId: number | string) => {
    const { dispatch, sitemap } = this.props;
    const target = (Array.isArray(sitemap) ? sitemap : []).find(
      s => s.id && s.id.toString() === newId.toString(),
    );
    const url = prop(target, 'url');

    if (url) {
      dispatch(push({ pathname: `/${url}`, search: '' }));
    }
  };
}

const mapStateToProps = (state, ownProps) => ({
  sitemap: sitemapSelector(state, ownProps.hubId),
  allArticles: allArticlesSelector(state, ownProps.hubId),
  isFetchingArticles: articlesIsFetchingSelector(state, ownProps.hubId),
  sitemapsById: sitemapByIdSelector(state, ownProps.hubId),
  lang: state.general.lang,
});

export default connect(mapStateToProps)(
  connectSsr({ displayName: 'CMSSectionArticles' })(
    withRouter(CMSSectionArticles),
  ),
);

const Wrapper = styled(FlexRow)`
  width: 100%;
  padding: ${rem(56)} ${rem(24)};

  ${({ theme }) => theme.mediab.l925`
    flex-flow: column;
    padding: 0 ${rem(24)} ${rem(32)};
  `}
`;

const ContentWrapper = styled(FlexCol)`
  width: 100%;
  justify-content: flex-end;

  ${({ theme }) => theme.mediab.l925`
     justify-content: center;
  `}
`;

const ArticlesWrapper = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(1048)};
  margin-right: 0;
  margin-left: auto;
`;
